import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppFloat from '@/components/feature/WhatsAppFloat';
import { ebooks, Ebook } from '@/mocks/ebooks';
import { useSEO } from '@/hooks/useSEO';
import EbookCover from '@/pages/ebooks/components/EbookCover';

interface FormState {
  loading: boolean;
  success: boolean;
  error: string;
}

function EbookModal({ ebook, onClose }: { ebook: Ebook; onClose: () => void }) {
  const [form, setForm] = useState({ name: '', email: '', whatsapp: '', empresa: '' });
  const [state, setState] = useState<FormState>({ loading: false, success: false, error: '' });
  const formRef = useRef<HTMLFormElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      setState(prev => ({ ...prev, error: 'Preencha nome e e-mail para continuar.' }));
      return;
    }
    setState({ loading: true, success: false, error: '' });
    try {
      const body = new URLSearchParams();
      body.append('name', form.name);
      body.append('email', form.email);
      body.append('whatsapp', form.whatsapp);
      body.append('empresa', form.empresa);
      body.append('ebook', ebook.title);
      const res = await fetch(ebook.formUrl, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: body.toString() });
      if (res.ok) {
        setState({ loading: false, success: true, error: '' });
      } else {
        setState({ loading: false, success: false, error: 'Algo deu errado. Tente novamente.' });
      }
    } catch {
      setState({ loading: false, success: false, error: 'Erro de conexão. Verifique sua internet.' });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-foreground-950/60 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-white rounded-2xl w-full max-w-lg overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-foreground-950 p-6 relative">
          <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-white/50 hover:text-white transition-colors cursor-pointer">
            <i className="ri-close-line text-xl"></i>
          </button>
          <div className="flex items-start gap-4">
            <div className="w-16 h-20 flex-shrink-0 rounded-lg overflow-hidden">
              <EbookCover ebookId={ebook.id} badge={ebook.badge} />
            </div>
            <div>
              <span className="inline-block text-primary-500 text-[11px] font-semibold uppercase tracking-[0.15em] mb-1">{ebook.category}</span>
              <h3 className="text-white font-semibold text-base leading-snug">
                {ebook.title}
              </h3>
              <p className="text-white/50 text-xs mt-1">{ebook.pages} páginas · Download gratuito</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {state.success ? (
            <div className="text-center py-4">
              <div className="w-16 h-16 flex items-center justify-center bg-primary-50 rounded-full mx-auto mb-4">
                <i className="ri-check-line text-primary-600 text-3xl"></i>
              </div>
              <h4 className="text-xl font-semibold text-foreground-900 mb-2">
                Tudo certo! Clique para baixar
              </h4>
              <p className="text-foreground-500 text-sm mb-6 font-light">
                Seu ebook está pronto. Clique no botão abaixo, depois use <strong className="text-foreground-800">Ctrl+P &rarr; Salvar como PDF</strong> para guardar no seu computador.
              </p>
              <a
                href={`${window.location.origin}${ebook.downloadUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full inline-flex items-center justify-center gap-2 bg-primary-600 text-white font-medium px-6 py-3.5 rounded-full hover:bg-primary-700 transition-colors whitespace-nowrap cursor-pointer text-sm mb-3"
              >
                <i className="ri-download-2-line text-lg"></i>
                Baixar {ebook.title}
              </a>
              <p className="text-foreground-400 text-xs mb-4">Arquivo PDF · Abre em qualquer dispositivo</p>
              <div className="border-t border-background-200/70 pt-4">
                <p className="text-foreground-500 text-xs mb-3">Ficou com dúvida ou quer uma consultoria?</p>
                <a
                  href="https://wa.me/5581982030285?text=Ol%C3%A1!%20Acabei%20de%20baixar%20o%20ebook%20e%20gostaria%20de%20saber%20mais%20sobre%20os%20m%C3%B3veis"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-foreground-900 text-white font-medium px-5 py-2.5 rounded-full hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  <i className="ri-whatsapp-line"></i>
                  Chamar no WhatsApp
                </a>
              </div>
            </div>
          ) : (
            <>
              <h4 className="text-foreground-900 font-semibold text-base mb-1">Preencha para receber gratuitamente</h4>
              <p className="text-foreground-400 text-xs mb-5">Sem spam. Só conteúdo relevante para o seu negócio.</p>
              <form ref={formRef} data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-foreground-700 mb-1.5">Nome completo *</label>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Seu nome"
                    className="w-full border border-background-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-primary-500 transition-colors"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-700 mb-1.5">E-mail *</label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="seu@email.com"
                    className="w-full border border-background-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-primary-500 transition-colors"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-700 mb-1.5">WhatsApp</label>
                  <input
                    type="text"
                    name="whatsapp"
                    value={form.whatsapp}
                    onChange={handleChange}
                    placeholder="(81) 9 0000-0000"
                    className="w-full border border-background-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-primary-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-700 mb-1.5">Empresa</label>
                  <input
                    type="text"
                    name="empresa"
                    value={form.empresa}
                    onChange={handleChange}
                    placeholder="Nome da sua empresa (opcional)"
                    className="w-full border border-background-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-primary-500 transition-colors"
                  />
                </div>
                {state.error && (
                  <p className="text-red-500 text-xs">{state.error}</p>
                )}
                <button
                  type="submit"
                  disabled={state.loading}
                  className="w-full bg-primary-600 text-white font-medium py-3.5 rounded-full hover:bg-primary-700 transition-colors flex items-center justify-center gap-2 cursor-pointer disabled:opacity-60 text-sm whitespace-nowrap"
                >
                  {state.loading ? (
                    <><i className="ri-loader-4-line animate-spin"></i> Enviando...</>
                  ) : (
                    <><i className="ri-download-2-line"></i> Baixar Ebook Grátis</>
                  )}
                </button>
                <p className="text-center text-xs text-foreground-400">
                  <i className="ri-lock-line mr-1"></i>
                  Seus dados estão protegidos. Nunca enviamos spam.
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function EbookCard({ ebook, onDownload }: { ebook: Ebook; onDownload: (e: Ebook) => void }) {
  return (
    <div className="flex flex-col group">
      {/* Cover */}
      <div className="relative bg-foreground-950 rounded-2xl p-8 flex justify-center">
        <div className="w-36 h-52 rounded-xl overflow-hidden group-hover:scale-[1.03] transition-transform duration-500">
          <EbookCover ebookId={ebook.id} badge={ebook.badge} />
        </div>
      </div>

      {/* Info */}
      <div className="pt-5 flex flex-col flex-1">
        <span className="text-primary-600 text-[11px] font-semibold uppercase tracking-[0.15em]">
          {ebook.category}
        </span>
        <h3 className="mt-2 text-lg font-semibold text-foreground-900 leading-snug">
          {ebook.title}
        </h3>
        <p className="mt-2 text-sm text-foreground-500 leading-relaxed font-light flex-1">
          {ebook.subtitle}
        </p>

        {/* Topics */}
        <ul className="mt-4 mb-6 space-y-2">
          {ebook.topics.slice(0, 4).map((topic, i) => (
            <li key={i} className="flex items-start gap-2 text-xs text-foreground-500 font-light">
              <i className="ri-check-line text-primary-600 mt-0.5"></i>
              <span>{topic}</span>
            </li>
          ))}
          {ebook.topics.length > 4 && (
            <li className="text-xs text-primary-600 font-medium pl-5">
              +{ebook.topics.length - 4} tópicos adicionais
            </li>
          )}
        </ul>

        {/* Footer */}
        <div className="border-t border-background-200/70 pt-4 flex items-center justify-between">
          <span className="text-xs text-foreground-400 flex items-center gap-1">
            <i className="ri-pages-line"></i>
            {ebook.pages} páginas
          </span>
          <button
            onClick={() => onDownload(ebook)}
            className="text-primary-600 text-sm font-medium hover:gap-2 flex items-center gap-1.5 transition-all cursor-pointer whitespace-nowrap"
          >
            Baixar Grátis
            <i className="ri-arrow-right-line"></i>
          </button>
        </div>
      </div>
    </div>
  );
}

export default function EbooksPage() {
  const [selectedEbook, setSelectedEbook] = useState<Ebook | null>(null);

  useSEO({
    title: 'Ebooks Gratuitos | Ergonomia, NR-17 e Escritório Corporativo – Home Office Premium',
    description: 'Baixe gratuitamente nossos ebooks sobre ergonomia, NR-17, montagem de escritórios e recepção profissional. Conteúdo especializado para empresas em Caruaru e Agreste de PE.',
    keywords: 'ebook ergonomia escritório, guia NR-17 gratuito, como montar escritório, móveis corporativos Caruaru',
    path: '/ebooks',
    ogTitle: 'Ebooks Gratuitos – Home Office Premium',
    ogDescription: 'Guias práticos sobre ergonomia, NR-17 e design de escritórios. Baixe gratuitamente.',
  });

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL ?? '';

    const breadcrumbId = 'schema-ebooks-breadcrumb';
    let bcScript = document.getElementById(breadcrumbId) as HTMLScriptElement | null;
    if (!bcScript) { bcScript = document.createElement('script'); bcScript.id = breadcrumbId; bcScript.type = 'application/ld+json'; document.head.appendChild(bcScript); }
    bcScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Ebooks Gratuitos', item: `${siteUrl}/ebooks` },
      ],
    });

    const faqId = 'schema-ebooks-faq';
    let faqScript = document.getElementById(faqId) as HTMLScriptElement | null;
    if (!faqScript) { faqScript = document.createElement('script'); faqScript.id = faqId; faqScript.type = 'application/ld+json'; document.head.appendChild(faqScript); }
    faqScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Os ebooks da Home Office Premium são realmente gratuitos?',
          acceptedAnswer: { '@type': 'Answer', text: 'Sim. Todos os ebooks da Home Office Premium são 100% gratuitos. Basta preencher um formulário rápido com nome e e-mail para ter acesso imediato ao download. Não enviamos spam.' },
        },
        {
          '@type': 'Question',
          name: 'O que é a NR-17 e por que ela importa para minha empresa?',
          acceptedAnswer: { '@type': 'Answer', text: 'A NR-17 é a Norma Regulamentadora do Ministério do Trabalho que estabelece parâmetros mínimos de ergonomia para o trabalho. Empresas que não atendem a NR-17 podem ser autuadas em fiscalizações. Nosso ebook gratuito explica como adequar seu escritório à norma.' },
        },
        {
          '@type': 'Question',
          name: 'Como montar um escritório corporativo do zero em Caruaru?',
          acceptedAnswer: { '@type': 'Answer', text: 'Nosso ebook "Escritório do Zero" cobre todo o processo: planejamento do layout, escolha dos móveis, ergonomia, iluminação e orçamento. É baseado em mais de 500 projetos entregues em Caruaru e Pernambuco.' },
        },
        {
          '@type': 'Question',
          name: 'A Home Office Premium oferece consultoria presencial em Caruaru?',
          acceptedAnswer: { '@type': 'Answer', text: 'Sim. Além dos ebooks gratuitos, oferecemos visita técnica gratuita para empresas em Caruaru e região. Nossa equipe avalia o espaço e apresenta um projeto personalizado sem compromisso.' },
        },
      ],
    });

    return () => { bcScript?.remove(); faqScript?.remove(); };
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <WhatsAppFloat />

      {/* Hero */}
      <section className="relative pt-36 md:pt-44 pb-20 md:pb-24 overflow-hidden bg-foreground-950">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8">
          <span className="inline-flex items-center gap-2 text-primary-500 text-[11px] font-semibold uppercase tracking-[0.2em] mb-6">
            <i className="ri-book-open-line"></i>
            Materiais Gratuitos
          </span>
          <h1 className="text-[36px] md:text-[56px] font-semibold text-white leading-[1.05] tracking-tight max-w-3xl">
            Ebooks gratuitos para transformar seu escritório.
          </h1>
          <p className="mt-6 text-[15px] md:text-base text-white/60 leading-relaxed max-w-xl font-light">
            Guias práticos e diretos ao ponto sobre ergonomia, NR-17, design de escritórios e experiência do cliente. Baixe grátis, aplique hoje.
          </p>
          <div className="mt-8 flex flex-wrap gap-6 text-sm text-white/50">
            <span className="flex items-center gap-2">
              <i className="ri-download-2-line text-primary-500"></i>
              Download imediato
            </span>
            <span className="flex items-center gap-2">
              <i className="ri-lock-line text-primary-500"></i>
              100% gratuito
            </span>
            <span className="flex items-center gap-2">
              <i className="ri-shield-check-line text-primary-500"></i>
              Sem spam, prometido
            </span>
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="bg-white border-b border-background-200/70">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-8">
          <div className="flex flex-wrap gap-x-12 gap-y-6">
            {[
              { icon: 'ri-book-2-line', value: '10', label: 'Ebooks disponíveis' },
              { icon: 'ri-group-line', value: '+1.200', label: 'Downloads realizados' },
              { icon: 'ri-star-line', value: '4.9★', label: 'Avaliação média' },
              { icon: 'ri-time-line', value: '15 min', label: 'Leitura média' },
            ].map((s, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-primary-50 rounded-lg">
                  <i className={`${s.icon} text-primary-600 text-lg`}></i>
                </div>
                <div>
                  <p className="text-foreground-900 font-semibold text-base">{s.value}</p>
                  <p className="text-foreground-400 text-xs">{s.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ebook Grid */}
      <section className="py-16 md:py-20">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8">
          <div className="text-center mb-14">
            <h2 className="text-[28px] md:text-[38px] font-semibold text-foreground-900 leading-[1.1] tracking-tight">
              Escolha seu próximo guia
            </h2>
            <p className="mt-4 text-foreground-500 font-light">Preencha um formulário rápido e receba o ebook no seu e-mail em segundos.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-8 gap-y-14">
            {ebooks.map(ebook => (
              <EbookCard key={ebook.id} ebook={ebook} onDownload={setSelectedEbook} />
            ))}
          </div>
        </div>
      </section>

      {/* What you'll learn strip */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-primary-600 text-[11px] font-semibold uppercase tracking-[0.2em]">Por que baixar</span>
              <h2 className="mt-4 text-[28px] md:text-[36px] font-semibold text-foreground-900 leading-[1.12] tracking-tight">
                Conteúdo feito por quem montou mais de 500 escritórios em Pernambuco
              </h2>
              <p className="mt-5 text-foreground-500 leading-relaxed font-light">
                Não é conteúdo genérico tirado da internet. São guias baseados em projetos reais entregues para empresas em Caruaru, Recife e no Agreste — com os erros, os acertos e o que realmente funciona na prática.
              </p>
              <ul className="mt-6 space-y-3">
                {[
                  'Linguagem direta — sem enrolação e sem jargão técnico',
                  'Exemplos reais de empresas em Caruaru e Agreste de PE',
                  'Checklists aplicáveis imediatamente',
                  'Estimativas de investimento honestas e atualizadas',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-foreground-600">
                    <i className="ri-check-line text-primary-600 mt-0.5"></i>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <div className="img-zoom rounded-2xl overflow-hidden aspect-[7/5] bg-background-100">
                <img
                  src="/media/ia/0292.webp"
                  alt="Lendo ebook de ergonomia"
                  loading="lazy"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial / Social proof */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8">
          <div className="text-center mb-12">
            <h2 className="text-[24px] md:text-[32px] font-semibold text-foreground-900 leading-[1.1] tracking-tight">
              O que dizem quem já leu
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                quote: 'O guia de NR-17 me poupou de uma autuação. Apliquei o checklist e adequamos as cadeiras antes da fiscalização aparecer.',
                name: 'Marcela R.',
                role: 'Gestora de RH, Caruaru',
              },
              {
                quote: 'Montei meu escritório de advocacia seguindo o ebook. Fechei dois contratos novos nas primeiras semanas depois da reforma.',
                name: 'Dr. Felipe A.',
                role: 'Advogado, Caruaru',
              },
              {
                quote: 'O ebook de recepção me ajudou a criar uma sala de espera que meus pacientes sempre elogiam. Melhorou minhas avaliações no Google.',
                name: 'Dra. Camila V.',
                role: 'Médica, Caruaru',
              },
            ].map((t, i) => (
              <div key={i} className="bg-background-50 rounded-2xl p-7">
                <div className="flex gap-1 mb-3">
                  {[...Array(5)].map((_, j) => (
                    <i key={j} className="ri-star-fill text-primary-500 text-xs"></i>
                  ))}
                </div>
                <p className="text-foreground-600 text-sm leading-relaxed mb-5 font-light">&quot;{t.quote}&quot;</p>
                <div>
                  <p className="text-foreground-900 font-semibold text-sm">{t.name}</p>
                  <p className="text-foreground-400 text-xs">{t.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Bottom */}
      <section className="bg-foreground-950">
        <div className="max-w-2xl mx-auto px-5 md:px-8 py-20 md:py-28 text-center">
          <h2 className="text-[28px] md:text-[36px] font-semibold text-white leading-[1.1] tracking-tight">
            Prefere uma consultoria personalizada?
          </h2>
          <p className="mt-4 text-white/50 font-light max-w-xl mx-auto">
            Nossos especialistas visitam seu espaço, avaliam cada detalhe e apresentam um projeto sob medida — sem compromisso e sem custo.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/contato"
              className="inline-flex items-center justify-center gap-2 bg-primary-600 text-white font-medium px-8 py-3.5 rounded-full hover:bg-primary-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Agendar Visita Técnica Gratuita
            </Link>
            <a
              href="https://wa.me/5581982030285?text=Ol%C3%A1!%20Baixei%20um%20dos%20ebooks%20e%20gostaria%20de%20uma%20consultoria"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-white/10 border border-white/20 text-white font-medium px-8 py-3.5 rounded-full hover:bg-white/20 transition-colors whitespace-nowrap cursor-pointer"
            >
              Chamar no WhatsApp
            </a>
          </div>
        </div>
      </section>

      <Footer />

      {/* Modal */}
      {selectedEbook && (
        <EbookModal ebook={selectedEbook} onClose={() => setSelectedEbook(null)} />
      )}
    </div>
  );
}